import React from "react";
import useDeleteNotification from "../../hooks/useDeleteNotification";
import useGetNotifications from "../../hooks/useGetNotifications";
import { useTimeout } from "../../hooks/useTimeout";
import { CheckCircleIcon, WarningIcon, ErrorIcon, CloseIcon } from "../icons";

type NotificationProps = {
  status: "success" | "warning" | "error";
  title: string;
  body?: string;
  timeout?: number;
  onClose: () => void;
};

export const Notification: React.FC<NotificationProps> = React.memo(
  ({ status, title, body, timeout, onClose }) => {
    useTimeout(onClose, timeout);

    return (
      <div className="fixed inset-0 z-20 flex items-end justify-center px-4 py-6 pointer-events-none sm:mt-20 sm:p-6 sm:items-start sm:justify-end" id={`notification-${status}`}>
        <div className="w-full max-w-sm bg-white rounded-lg shadow-lg pointer-events-auto">
          <div className="overflow-hidden rounded-lg shadow-xs">
            <div className="p-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  {status === "success" && (
                    <CheckCircleIcon iconClassName="w-6 h-6 text-green-500" />
                  )}
                  {status === "warning" && (
                    <WarningIcon iconClassName="w-6 h-6 text-orange-500" />
                  )}
                  {status === "error" && (
                    <ErrorIcon iconClassName="w-6 h-6 text-red-500" />
                  )}
                </div>
                <div className="ml-3 w-0 flex-1 pt-0.5">
                  <p className="text-sm font-medium leading-5 text-gray-900">
                    {title}
                  </p>
                  <p className="mt-1 text-sm leading-5 text-gray-500">{body}</p>
                </div>
                <div className="flex flex-shrink-0 ml-4">
                  <button
                    className="inline-flex text-gray-400 transition duration-150 ease-in-out focus:outline-none focus:text-gray-500"
                    onClick={onClose}
                  >
                    <CloseIcon iconClassName="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

const NotificationContainer: React.FC = () => {
  const { data } = useGetNotifications();
  const notifications = data?.notifications || [];

  const [deleteNotification] = useDeleteNotification();

  return notifications.map(({ body, title, status, id }) => (
    <Notification
      key={id}
      title={title}
      body={body}
      status={status}
      onClose={() => {
        deleteNotification({ variables: { id } });
      }}
    />
  ));
};

export default NotificationContainer;
